import React from "react";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useLanguage from "../../hooks/useLanguage.js";
import useScrollToHomeSection from "../../hooks/useScrollToHomeSection.js";
import { trackCTA, trackLead } from "../../lib/analytics";
import { buildSandboxUrl } from "../../lib/sandboxDemo.js";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";

/**
 * Page hero. Same shape as the photo-sharing and invitation heroes — eyebrow,
 * H1 at `heading-9vw`, supporting line, two actions — so the three SEO pages
 * read as chapters of one story rather than three designs.
 *
 * The secondary action is the sandbox, and it is the strongest asset this page
 * has for the same reason the sample invitation is the invitation page's: the
 * Planning Hub is a workspace, and no photograph of a wedding can show one.
 * `trackLead` fires alongside `trackCTA` here — unlike the invitation preview,
 * `/try` provisions a real (disposable) Heirloom event, which is the site's own
 * definition of a lead and exactly how the homepage pricing card already treats
 * the same destination.
 *
 * No `data-w-id` and no inline `opacity: 0`, for the reason documented on the
 * photo-sharing hero: the H1 must never depend on the Webflow interaction
 * engine to become visible.
 */
const PlanningHero = () => {
  const { t } = useTranslation();
  const localize = useLocalizedHref();
  const { shortLanguage } = useLanguage();
  const analyticsContext = useAnalyticsContext();
  const scrollToPlans = useScrollToHomeSection("pricing");

  return (
    <header className="section wps-hero">
      <div className="container">
        <p className="wps-eyebrow">{t("weddingPlanning.hero.eyebrow")}</p>
        <h1 className="heading-9vw wps-hero-heading">
          {t("weddingPlanning.hero.heading")}
        </h1>
        <p className="body-20 wps-hero-body">{t("weddingPlanning.hero.body")}</p>
        <div className="wps-hero-actions">
          <Button
            href={localize("/#pricing")}
            text={t("weddingPlanning.hero.ctaPrimary")}
            className="button-lg"
            onClick={(e) => {
              trackCTA("wedding_planning_hero", {
                ...analyticsContext,
                ctaName: "see_plans",
              });
              scrollToPlans(e);
            }}
          />
          <a
            className="wps-text-link"
            href={buildSandboxUrl(shortLanguage)}
            target="_blank"
            rel="noopener noreferrer nofollow"
            onClick={() => {
              const ctx = { ...analyticsContext, ctaName: "start_trial" };
              trackCTA("wedding_planning_hero", ctx);
              trackLead("wedding_planning_hero", ctx);
            }}
          >
            {t("weddingPlanning.hero.ctaSecondary")}
          </a>
        </div>
      </div>
    </header>
  );
};

export default PlanningHero;
